import Phaser from 'phaser';

export type MeleeAttackSprite = {
    sprite: Phaser.GameObjects.Sprite;
    startTime: number;
    duration: number;
};

export class MeleeAttackSpriteManager {
    private scene: Phaser.Scene;
    private activeSprites: MeleeAttackSprite[] = [];
    private readonly DISPLAY_DURATION = 200; // 200ms as requested

    constructor(scene: Phaser.Scene) {
        this.scene = scene;
    }

    /**
     * Display a melee attack sprite at the attacker's position, rotated towards the target
     */
    showMeleeAttack(attackerX: number, attackerY: number, targetX: number, targetY: number) {
        // Calculate rotation angle towards the target
        const dx = targetX - attackerX;
        const dy = targetY - attackerY;
        const angle = Math.atan2(dy, dx);

        // Calculate position 20 pixels away from the player towards the target
        const spriteX = attackerX + Math.cos(angle) * 20;
        const spriteY = attackerY + Math.sin(angle) * 20;

        // Create the melee attack sprite
        const sprite = this.scene.add.sprite(spriteX, spriteY, 'melee-attack');
        sprite.setOrigin(0.5, 0.5);
        sprite.setRotation(angle);
        sprite.setDepth(15); // Above players and enemies

        // Make sure UI camera ignores this sprite (game world element)
        if ((this.scene as any).uiCamera) {
            (this.scene as any).uiCamera.ignore(sprite);
        }

        // Start at quarter scale and fully transparent
        sprite.setScale(0.5); // Quarter of the original 2x scale
        sprite.setAlpha(0);

        // Create animation timeline
        this.scene.tweens.add({
            targets: sprite,
            scaleX: 2, // Grow to full scale
            scaleY: 2,
            alpha: 1, // Fade to fully visible
            duration: 100, // First half of animation (fade in + grow)
            ease: 'Power2',
            onComplete: () => {
                // Second half: fade out
                this.scene.tweens.add({
                    targets: sprite,
                    alpha: 0,
                    duration: 100, // Second half of animation (fade out)
                    ease: 'Power2',
                    onComplete: () => {
                        sprite.destroy();
                    }
                });
            }
        });

        // Add to active sprites for cleanup (in case scene is destroyed before animation completes)
        this.activeSprites.push({
            sprite,
            startTime: this.scene.time.now,
            duration: this.DISPLAY_DURATION
        });
    }

    /**
     * Update method to be called each frame - removes expired sprites
     * Note: Sprites are now automatically destroyed by animation, but this provides cleanup safety
     */
    update() {
        const now = this.scene.time.now;

        this.activeSprites = this.activeSprites.filter(meleeSprite => {
            const elapsed = now - meleeSprite.startTime;

            if (elapsed > meleeSprite.duration) {
                // Safety cleanup - destroy sprite if it somehow wasn't destroyed by animation
                if (meleeSprite.sprite && meleeSprite.sprite.active) {
                    meleeSprite.sprite.destroy();
                }
                return false;
            }

            return true;
        });
    }

    /**
     * Clean up all active sprites (useful for scene cleanup)
     */
    destroy() {
        this.activeSprites.forEach(meleeSprite => {
            meleeSprite.sprite.destroy();
        });
        this.activeSprites = [];
    }
}